/**
 * BottomNav - Reddit-style bottom navigation bar
 * 
 * Clean, minimal, sticky navigation for mobile-first experience.
 * Shows on all user-facing pages (not admin).
 * 
 * Feature flags control visibility of:
 * - Inbox (inbox_enabled)
 * - Ranks/Leaderboard (leaderboard_enabled)
 * - Map (maps_enabled)
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useProtectedFeature } from '../../hooks/useProtectedFeature';
import ProtectedFeatureButton from '../auth/ProtectedFeatureButton';

const BottomNav = ({ unreadInboxCount = 0, onInboxClick, onMapClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isInboxEnabled, isLeaderboardEnabled, isMapsEnabled } = useFeatureFlags();
  const { isAuthenticated } = useProtectedFeature();
  
  // Hide on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }
  
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  // Icons (Simple SVG paths)
  const Icons = {
    Camera: () => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
      </svg>
    ),
    Mail: () => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    // Podium/Leaderboard icon - cleaner representation of rankings
    Podium: () => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        {/* 2nd place - left bar */}
        <rect x="3" y="10" width="5" height="10" rx="1" strokeLinecap="round" strokeLinejoin="round" />
        {/* 1st place - center bar (tallest) */}
        <rect x="9.5" y="4" width="5" height="16" rx="1" strokeLinecap="round" strokeLinejoin="round" />
        {/* 3rd place - right bar */}
        <rect x="16" y="13" width="5" height="7" rx="1" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    Map: () => (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
      </svg>
    )
  };

  // Build nav items based on feature flags
  const allNavItems = [
    {
      id: 'camera',
      path: '/',
      icon: <Icons.Camera />,
      label: 'Scan',
      onClick: null,
      enabled: true // Always show camera/scan
    },
    {
      id: 'inbox',
      path: null, // No navigation, opens modal
      icon: <Icons.Mail />,
      label: 'Inbox',
      badge: unreadInboxCount,
      onClick: onInboxClick,
      enabled: isInboxEnabled
    },
    {
      id: 'leaderboard',
      path: '/leaderboard',
      icon: <Icons.Podium />,
      label: 'Ranks',
      onClick: null,
      enabled: isLeaderboardEnabled
    },
    {
      id: 'map',
      path: null, // No navigation, opens modal
      icon: <Icons.Map />,
      label: 'Map',
      onClick: onMapClick,
      enabled: isMapsEnabled
    }
  ];
  
  // Filter to only show enabled nav items
  const navItems = allNavItems.filter(item => item.enabled);
  
  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#001f35] border-t-2 border-[#0DB14B]/40 shadow-[0_-4px_30px_rgba(0,0,0,0.8)]" 
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
        background: 'linear-gradient(180deg, rgba(0,31,53,0.98) 0%, rgba(0,49,79,1) 100%)'
      }}
    >
      <div className="flex items-center justify-around px-2 py-1 max-w-screen-lg mx-auto">
        {navItems.map((item) => {
          const active = item.path && isActive(item.path);
          const isProtected = ['inbox', 'leaderboard', 'map'].includes(item.id);
          
          // Camera/Scan is always unprotected
          if (item.id === 'camera') {
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                className={`
                  flex flex-col items-center justify-center gap-0.5 px-4 py-1 rounded-lg transition-all relative w-16
                  ${active
                    ? 'text-white'
                    : 'text-white/50 hover:text-white/80'
                  }
                `}
              >
                <div className={`transition-transform duration-200 ${active ? 'scale-110' : 'scale-100'}`}>
                  {item.icon}
                </div>
                <span className={`text-[10px] font-medium ${active ? 'text-white' : 'text-white/60'}`}>
                  {item.label}
                </span>
              </button>
            );
          }
          
          // Protected features (Inbox, Ranks, Map)
          if (isProtected) {
            return (
              <ProtectedFeatureButton
                key={item.id}
                featureName={item.label}
                onAccess={() => {
                  if (item.onClick) {
                    item.onClick();
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
                showLockBadge={!isAuthenticated}
                className={`
                  flex flex-col items-center justify-center gap-0.5 px-4 py-1 rounded-lg transition-all relative w-16
                  ${active
                    ? 'text-white'
                    : 'text-white/50 hover:text-white/80'
                  }
                `}
              >
                {/* Icon */}
                <div className={`transition-transform duration-200 ${active ? 'scale-110' : 'scale-100'}`}>
                  {item.icon}
                </div>
                
                {/* Label */}
                <span className={`text-[10px] font-medium ${active ? 'text-white' : 'text-white/60'}`}>
                  {item.label}
                </span>
                
                {/* Badge (for inbox) */}
                {item.badge > 0 && (
                  <span className="absolute top-1 right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center shadow-sm">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </ProtectedFeatureButton>
            );
          }
          
          // Fallback (shouldn't reach here)
          return null;
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
